import React, { useEffect, useRef, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import Loader2 from "../components/Loader2";
import { useAuth } from "./authContext";
import { checkUserPaymentStatus } from "./paymentUtils.js";

/**
 * ProtectedRoute
 * - Auth gate
 * - Optional payment gate (requiredPayment)
 * - Guarded payment checks to avoid render loops / spam
 */
const ProtectedRoute = ({ requiredPayment = false }) => {
  const { user, loading } = useAuth();
  const [isPaid, setIsPaid] = useState(null); // null = unknown, boolean when known
  const paymentRequired = requiredPayment;

  // One-time mount log guard
  const loggedOnce = useRef(false);
  // Track in-flight payment checks without tying them to render state. This keeps the
  // loader logic accurate while preventing React StrictMode double-invocation from
  // leaving the component stuck in a perpetual "checking" state.
  const checkingPaymentRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    // If payment is not required for this route, mark as allowed and exit
    if (!paymentRequired) {
      if (!cancelled) {
        setIsPaid(true);
        checkingPaymentRef.current = false;
      }
      return () => {
        cancelled = true;
      };
    }

    // If no user, reset local state
    if (!user) {
      setIsPaid(null);
      checkingPaymentRef.current = false;
      return () => {
        cancelled = true;
      };
    }

    // Only check if we don't yet know the payment state and not already checking
    if (isPaid === null && !checkingPaymentRef.current) {
      checkingPaymentRef.current = true;
      checkUserPaymentStatus(user.uid, { forceRemote: false })
        .then((status) => {
          if (cancelled) return;
          setIsPaid(Boolean(status));
        })
        .catch((error) => {
          if (cancelled) return;
          console.error("❌ Error checking payment status:", error);
          setIsPaid(false);
        })
        .finally(() => {
          checkingPaymentRef.current = false;
        });
    }

    return () => {
      cancelled = true;
      checkingPaymentRef.current = false;
    };
    // isPaid is intentionally excluded: it's the trigger condition this
    // effect checks (isPaid === null), not something it should re-run on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, paymentRequired, setIsPaid]);

  // Prevent redirects until auth & (if required) payment state are resolved
  if (loading || (paymentRequired && (checkingPaymentRef.current || isPaid === null))) {
    if (!loggedOnce.current) {
      console.log("⏳ Waiting for authentication/payment state...");
      loggedOnce.current = true;
    }
    return <Loader2 text="Loading..." />;
  }

  // Redirect to login if user is not authenticated
  if (!user) {
    console.warn("🚫 User not authenticated. Redirecting to login...");
    return <Navigate to="/login" replace />;
  }

  // UX-only guard: backend callable checks remain authoritative for all paid features.
  if (paymentRequired && isPaid === false) {
    console.warn("💳 Payment required but not completed. Redirecting to onboarding overview...");
    return <Navigate to="/onboardingoverview" replace />;
  }

  if (!loggedOnce.current) {
    console.log("✅ User authenticated and payment verified (if required). Rendering page...");
    loggedOnce.current = true;
  }

  return <Outlet />;
};

export default ProtectedRoute;
